/**
 * Card availability, decided in one place.
 *
 * The rule that says whether a card can take a payment lives in
 * `packages/shared/src/ledger.ts` and is called — never re-implemented — here.
 * Routing runs the same function on the same figures, so a card can never sit
 * on «Реквизиты» claiming to be available while the router quietly skips it.
 */

import { CardStatus } from '@shared/types';
import {
  DEFAULT_CARD_LIMITS,
  cardBlockReason,
  type CardBlockReason,
  type CardUsage,
} from '@shared/ledger';
import type { ResolvedCard, TraderCard } from '@/types/api';

/** A server that has not shipped usage yet is treated as "nothing used". */
const NO_USAGE: CardUsage = { usedRub24h: 0, activeCount: 0 };

/**
 * Fill in the money fields an older server may omit.
 *
 * `isEnabled` defaults to true because the switch is the trader's own: a server
 * that does not know about it has not turned anything off, and defaulting to
 * false would show every card as disabled on the day the field ships.
 */
export function resolveCard(card: TraderCard): ResolvedCard {
  return {
    card,
    isEnabled: card.isEnabled ?? true,
    limits: card.limits ?? DEFAULT_CARD_LIMITS,
    usage: card.usage ?? NO_USAGE,
  };
}

/**
 * Why this card cannot take a payment right now, or `null` if it can.
 *
 * The server's own answer wins when it names a reason — only it knows about the
 * rolling 24h window, which `cardBlockReason` cannot judge without a specific
 * payment amount. When the server says nothing, or says nothing is wrong, the
 * shared function still gets the final word: erring towards "blocked" costs the
 * trader an explanation they can act on, while erring towards "available" costs
 * them payments they never see arrive.
 */
export function blockReasonFor(card: TraderCard): CardBlockReason | null {
  const { isEnabled, limits, usage } = resolveCard(card);

  return (
    card.blockReason ??
    cardBlockReason({
      isVerified: card.status === CardStatus.Verified,
      isEnabled,
      limits,
      usage,
    })
  );
}

/** 0 means unlimited throughout the money layer, so there is no bar to fill. */
export function usageRatio(used: number, limit: number): number | null {
  if (limit <= 0) return null;
  return Math.max(0, Math.min(1, used / limit));
}
